import React from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import BoxCanvas from '../../components/BoxCanvas';
import Breadcrumb from '../components/Breadcrumb';
import QueryError from '../components/QueryError';
import { CREATE_FIGURE, DELETE_FIGURE, UPDATE_FIGURE } from '../graphql/mutations';
import { PAGE_QUERY, PUBLICATION_QUERY, SITES_QUERY } from '../graphql/queries';

const TYPES = [
  { name: 'Spine', color: '#F44336' },
  { name: 'SkeletonFigure', color: '#9575CD' },
  { name: 'Arrow', color: '#009688' },
  { name: 'GraveCrossSection', color: '#26C6DA' },
  { name: 'Good', color: '#4CAF50' },
  { name: 'Scale', color: '#7E57C2' },
  { name: 'Ceramic', color: '#FF9800' },
  { name: 'StoneTool', color: '#90A4AE' }
];
const LABEL = (n) => (n === 'Good' ? 'Artefact' : n === 'StoneTool' ? 'Lithic' : n);

export default function PageShowPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    data: pageData,
    loading: pageLoading,
    error: pageError
  } = useQuery(PAGE_QUERY, { variables: { id } });
  const { data: pubData } = useQuery(PUBLICATION_QUERY, {
    variables: { id: pageData?.page?.publicationId },
    skip: !pageData?.page?.publicationId
  });
  const { data: sitesData } = useQuery(SITES_QUERY);

  const [createFigure] = useMutation(CREATE_FIGURE, {
    refetchQueries: [{ query: PAGE_QUERY, variables: { id } }]
  });
  const [deleteFigure] = useMutation(DELETE_FIGURE, {
    refetchQueries: [{ query: PAGE_QUERY, variables: { id } }]
  });
  const [updateFigure, { loading: saving }] = useMutation(UPDATE_FIGURE, {
    refetchQueries: [{ query: PAGE_QUERY, variables: { id } }]
  });

  if (pageLoading)
    return (
      <div className="page-viewer">
        <div className="page-viewer-loading">Loading...</div>
      </div>
    );
  if (pageError)
    return (
      <div className="page-viewer">
        <div className="page-viewer-error">
          <QueryError error={pageError} />
        </div>
      </div>
    );

  const page = pageData?.page;
  if (!page)
    return (
      <div className="page-viewer">
        <div className="page-viewer-error">Page not found.</div>
      </div>
    );

  const pages = pubData?.publication?.pages || [];
  const currentPageIndex = pages.findIndex((p) => p.id === page.id);
  const prevPage = currentPageIndex > 0 ? pages[currentPageIndex - 1] : null;
  const nextPage = currentPageIndex < pages.length - 1 ? pages[currentPageIndex + 1] : null;

  return (
    <div className="page-viewer">
      <div className="page-viewer-header">
        <Breadcrumb
          items={[
            { label: 'Publications', link: '/publications' },
            { label: `Page ${page.number + 1}` }
          ]}
        />
        <div className="d-flex gap-2">
          {prevPage ? (
            <Link to={`/pages/${prevPage.id}`} className="btn btn-secondary">
              <span className="material-symbols-rounded">chevron_left</span> Previous
            </Link>
          ) : (
            <button className="btn btn-secondary" disabled>
              <span className="material-symbols-rounded">chevron_left</span> Previous
            </button>
          )}
          {nextPage ? (
            <Link to={`/pages/${nextPage.id}`} className="btn btn-secondary">
              Next <span className="material-symbols-rounded">chevron_right</span>
            </Link>
          ) : (
            <button className="btn btn-secondary" disabled>
              Next <span className="material-symbols-rounded">chevron_right</span>
            </button>
          )}
        </div>
      </div>
      <PageViewer
        page={page}
        sites={sitesData?.sites}
        createFigure={createFigure}
        deleteFigure={deleteFigure}
        updateFigure={updateFigure}
        saving={saving}
      />
    </div>
  );
}

function PageViewer({ page, sites, createFigure, deleteFigure, updateFigure, saving }) {
  const [figures, setFigures] = React.useState({});
  const [currentEditBox, setCurrentEditBox] = React.useState(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const [newFigureType, setNewFigureType] = React.useState(null);
  const [newFigureCoords, setNewFigureCoords] = React.useState(null);
  const [image, setImage] = React.useState(null);
  const divRef = React.useRef(null);

  React.useEffect(() => {
    const pageFigures = {};
    page.figures.forEach((f) => {
      pageFigures[f.id] = f;
    });
    setFigures(pageFigures);
  }, [page.figures]);

  React.useEffect(() => {
    if (!page.imageUrl) return;
    const img = new window.Image();
    img.onload = () => setImage(img);
    img.src = page.imageUrl;
  }, [page.imageUrl]);

  React.useEffect(() => {
    if (divRef.current) {
      divRef.current.style.cursor = isDrawing ? 'crosshair' : 'default';
    }
  }, [isDrawing]);

  function startDrawing(type) {
    setIsDrawing(true);
    setNewFigureType(type);
    setNewFigureCoords(null);
    setCurrentEditBox(null);
  }

  function resetDraw() {
    setIsDrawing(false);
    setNewFigureType(null);
    setNewFigureCoords(null);
  }

  async function finishDrawing() {
    if (!isDrawing || !newFigureCoords || !newFigureType) {
      resetDraw();
      return;
    }
    if (newFigureCoords.x1 === newFigureCoords.x2 || newFigureCoords.y1 === newFigureCoords.y2) {
      resetDraw();
      return;
    }
    const coords = {
      x1: Math.min(newFigureCoords.x1, newFigureCoords.x2),
      y1: Math.min(newFigureCoords.y1, newFigureCoords.y2),
      x2: Math.max(newFigureCoords.x1, newFigureCoords.x2),
      y2: Math.max(newFigureCoords.y1, newFigureCoords.y2)
    };
    resetDraw();
    await createFigure({
      variables: {
        input: {
          pageId: page.id,
          type: newFigureType,
          x1: Math.round(coords.x1),
          y1: Math.round(coords.y1),
          x2: Math.round(coords.x2),
          y2: Math.round(coords.y2)
        }
      }
    });
  }

  function onChangeFigure(id, figure) {
    setFigures((prev) => ({ ...prev, [id]: figure }));
  }

  async function handleDeleteFigure(figureId) {
    if (confirm('Delete this figure?')) {
      await deleteFigure({ variables: { input: { id: figureId } } });
    }
  }

  async function handleUpdateFigure(figureId, updates) {
    await updateFigure({ variables: { input: { id: figureId, ...updates } } });
  }

  const activeFigure = currentEditBox ? figures[currentEditBox] : null;

  return (
    <div
      style={{
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--md-sys-color-surface-container)'
      }}
    >
      <div
        style={{
          position: 'sticky',
          top: 0,
          display: 'flex',
          zIndex: 999,
          padding: 'var(--md-sys-spacing-sm)',
          backgroundColor: 'var(--md-sys-color-surface-container-low)',
          borderBottom: '1px solid var(--md-sys-color-outline-variant)'
        }}
      >
        <div className="btn-group" role="group" aria-label="Figure types">
          {TYPES.map((t) => (
            <button
              key={t.name}
              className="btn btn-secondary"
              style={{ backgroundColor: t.color, color: 'white' }}
              onClick={() => startDrawing(t.name)}
            >
              {LABEL(t.name)}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0' }}>
        <div className="card m-3" ref={divRef} style={{ overflow: 'hidden', flex: '0 0 66.66%' }}>
          <h3 className="card-header">Page {page.number + 1}</h3>
          {image && (
            <BoxCanvas
              divRef={divRef}
              image={image}
              figures={figures}
              currentEditBox={currentEditBox}
              setCurrentEditBox={setCurrentEditBox}
              onChangeFigure={onChangeFigure}
              isDrawing={isDrawing}
              newFigureCoords={newFigureCoords}
              setNewFigureCoords={setNewFigureCoords}
              finishDrawing={finishDrawing}
            />
          )}
        </div>

        <div
          className="me-3"
          style={{
            position: 'sticky',
            top: 0,
            alignSelf: 'flex-start',
            maxHeight: 'calc(100vh - 60px)',
            overflowY: 'auto',
            flex: '0 0 33.33%'
          }}
        >
          <div className="card">
            <div className="card-body">
              <ul className="list-group">
                {Object.values(figures).map((figure) => (
                  <React.Fragment key={figure.id}>
                    <div
                      onClick={() => setCurrentEditBox(figure.id)}
                      className={`list-group-item list-group-item-action d-flex justify-content-between align-items-start ${
                        currentEditBox === figure.id ? 'active' : ''
                      }`}
                    >
                      <div className="ms-2 me-auto">
                        <div className="fw-bold">
                          {LABEL(figure.type)} {figure.id}
                        </div>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-danger rounded-pill"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteFigure(figure.id);
                        }}
                      >
                        <span className="material-symbols-rounded" style={{ fontSize: '16px' }}>
                          delete
                        </span>
                      </button>
                    </div>
                    {currentEditBox === figure.id && figure.type === 'SkeletonFigure' && (
                      <div className="list-group-item px-4">
                        <div className="row mb-2 mt-2">
                          <label className="col-4 col-form-label">Position</label>
                          <div className="col-8">
                            <select
                              value={figure.depositionType || 'unknown'}
                              className="form-select form-select-sm"
                              onChange={(e) =>
                                handleUpdateFigure(figure.id, { depositionType: e.target.value })
                              }
                            >
                              <option value="unknown">Unknown</option>
                              <option value="back">Back</option>
                              <option value="side">Side</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
